import { Component, OnInit, OnDestroy } from '@angular/core';
import { LanguageService } from '../../shared/services/language.service';
import { ApiEstimativaService } from '../../shared/services/api-estimativa.service';
import { TecnologiasService } from '../../shared/services/tecnologias.service';
import { forkJoin, Subscription } from 'rxjs';
import * as echarts from 'echarts';

@Component({
  standalone: false,
  selector: 'app-catalog-stats',
    templateUrl: './catalog-stats.component.html',
    styleUrls: ['./catalog-stats.component.css']
})
export class CatalogStatsComponent implements OnInit, OnDestroy {

    variables: any;
    catalogs: any[] = [];
    selectedCatalogId: string = '';
    selectedCatalog: any = null;

    // Raw data
    allEstimates: any[] = [];
    allImputations: any[] = [];
    catalogDetails: Map<string, any> = new Map();

    // Filtered data
    filteredEstimates: any[] = [];
    filteredImputations: any[] = [];

    // KPIs
    kpiProjects = 0;
    kpiTotalEstimated = 0;
    kpiTotalReal = 0;
    kpiDeviation = 0;
    kpiDeviationPercent = 0;

    // Detailed table data
    taskStats: any[] = [];
    projectList: any[] = [];
    recommendations: string[] = [];

    loading = true;

    private chartUnitEffort: echarts.ECharts | null = null;
    private chartDeviation: echarts.ECharts | null = null;
    private chartPrecision: echarts.ECharts | null = null;
    private langSub: Subscription | null = null;

    constructor(
        private languageService: LanguageService,
        private estimativaService: ApiEstimativaService,
        private tecnologiasService: TecnologiasService
    ) { }

    ngOnInit(): void {
        this.langSub = this.languageService.translations$.subscribe((vars: any) => {
            this.variables = vars;
        });
        this.loadData();
    }

    ngOnDestroy(): void {
        this.langSub?.unsubscribe();
        this.chartUnitEffort?.dispose();
        this.chartDeviation?.dispose();
        this.chartPrecision?.dispose();
    }

    private loadData(): void {
        this.loading = true;
        forkJoin({
            estimates: this.estimativaService.myEstimates(),
            imputations: this.estimativaService.getAllImputations(),
            catalogs: this.tecnologiasService.getUserTechnologies()
        }).subscribe({
            next: (data: any) => {
                this.allEstimates = Array.isArray(data.estimates) ? data.estimates : [];
                this.allImputations = Array.isArray(data.imputations) ? data.imputations : [];
                const cats = Array.isArray(data.catalogs) ? data.catalogs : [];
                this.catalogs = cats;

                // Build detail map keyed by catalog id
                cats.forEach((cat: any) => {
                    this.catalogDetails.set(cat.id, cat);
                });

                this.loading = false;

                // Auto-select first catalog if available
                if (this.catalogs.length > 0) {
                    this.selectedCatalogId = this.catalogs[0].id;
                    this.onCatalogChange();
                }
            },
            error: (err: any) => {
                console.error('Error loading data', err);
                this.loading = false;
            }
        });
    }

    onCatalogChange(): void {
        if (!this.selectedCatalogId) return;

        this.selectedCatalog = this.catalogDetails.get(this.selectedCatalogId) || null;
        const catalogName = this.selectedCatalog?.catalogName || this.selectedCatalog?.name || '';

        // Filter estimates by catalog
        // Note: est.catalog from myEstimates() API is a plain string (catalog name), not an object
        this.filteredEstimates = this.allEstimates.filter((est: any) => {
            if (typeof est.catalog === 'string') {
                return est.catalog === catalogName;
            }
            // Fallback for full estimate objects (different API)
            const estCatName = est.catalog?.catalogName || est.catalog?.name || '';
            const estCatId = est.catalog?.id || '';
            return estCatId === this.selectedCatalogId || estCatName === catalogName;
        });

        // Map estimate IDs for imputation matching
        const estimateIds = new Set(this.filteredEstimates.map((e: any) => e.id || e.idEstimate));

        this.filteredImputations = this.allImputations.filter((imp: any) =>
            estimateIds.has(imp.estimateId)
        );

        this.computeKPIs();
        this.computeTaskStats();
        this.buildProjectList();
        this.generateRecommendations();

        // Defer chart rendering
        setTimeout(() => {
            this.renderUnitEffortChart();
            this.renderDeviationChart();
            this.renderPrecisionChart();
        }, 100);
    }

    private computeKPIs(): void {
        this.kpiProjects = this.filteredEstimates.length;
        this.kpiTotalEstimated = this.filteredEstimates.reduce((sum: number, e: any) => sum + (e.totalEffortHours || 0), 0);
        this.kpiTotalReal = this.filteredImputations.reduce((sum: number, imp: any) => sum + (imp.totalActualHours || 0), 0);
        this.kpiDeviation = this.kpiTotalReal - this.kpiTotalEstimated;
        this.kpiDeviationPercent = this.kpiTotalEstimated > 0
            ? ((this.kpiDeviation) / this.kpiTotalEstimated) * 100
            : 0;
    }

    private computeTaskStats(): void {
        // Get base effort from catalog actionItems
        const baseEffortMap = new Map<string, { low: number; med: number; high: number }>();
        if (this.selectedCatalog?.actionItems) {
            this.selectedCatalog.actionItems.forEach((item: any) => {
                baseEffortMap.set(item.name, {
                    low: parseFloat(item.complexityLow) || 0,
                    med: parseFloat(item.complexityMedium) || 0,
                    high: parseFloat(item.complexityHigh) || 0
                });
            });
        }

        // Aggregate imputation data by section name (task name)
        const taskMap = new Map<string, { count: number; totalEst: number; totalReal: number }>();
        this.filteredImputations.forEach((imp: any) => {
            if (!imp.imputations) return;
            imp.imputations.forEach((item: any) => {
                const name = item.sectionName || item.groupName || 'Otros';
                if (!taskMap.has(name)) {
                    taskMap.set(name, { count: 0, totalEst: 0, totalReal: 0 });
                }
                const entry = taskMap.get(name)!;
                entry.count++;
                entry.totalEst += (item.estimatedHours || 0);
                entry.totalReal += (item.actualHours || 0);
            });
        });

        this.taskStats = [];
        taskMap.forEach((val, name) => {
            const base = baseEffortMap.get(name);
            const avgBase = base ? (base.low + base.med + base.high) / 3 : 0;
            const avgReal = val.count > 0 ? val.totalReal / val.count : 0;
            const deviation = val.totalEst > 0
                ? ((val.totalReal - val.totalEst) / val.totalEst) * 100
                : 0;
            this.taskStats.push({
                name,
                projects: val.count,
                avgBase: avgBase,
                avgReal: avgReal,
                totalEst: val.totalEst,
                totalReal: val.totalReal,
                deviation: deviation
            });
        });

        // Sort by absolute deviation descending
        this.taskStats.sort((a: any, b: any) => Math.abs(b.deviation) - Math.abs(a.deviation));
    }

    private buildProjectList(): void {
        this.projectList = this.filteredEstimates.map((est: any) => {
            const imp = this.filteredImputations.find((i: any) => i.estimateId === (est.id || est.idEstimate));
            return {
                name: est.name || est.idEstimate || '—',
                state: est.state || '—',
                estimated: est.totalEffortHours || 0,
                real: imp?.totalActualHours ?? null,
                deviation: imp && est.totalEffortHours
                    ? ((imp.totalActualHours - est.totalEffortHours) / est.totalEffortHours) * 100
                    : null,
                createdDate: est.createdDate || null
            };
        });
    }

    private generateRecommendations(): void {
        this.recommendations = [];

        if (this.filteredImputations.length === 0) {
            this.recommendations.push('No hay datos de imputación real para este catálogo. Registra imputaciones para obtener análisis de precisión.');
            return;
        }

        // High deviation tasks (>25%)
        const highDev = this.taskStats.filter((t: any) => Math.abs(t.deviation) > 25);
        if (highDev.length > 0) {
            const names = highDev.slice(0, 5).map((t: any) => `"${t.name}" (${t.deviation > 0 ? '+' : ''}${t.deviation.toFixed(1)}%)`).join(', ');
            this.recommendations.push(
                `⚠️ Las siguientes tareas tienen una desviación superior al 25%: ${names}. Considera ajustar los esfuerzos base del catálogo para estas tareas.`
            );
        }

        // Under-estimated (real > estimated, consistently)
        const underEstimated = this.taskStats.filter((t: any) => t.deviation > 15);
        if (underEstimated.length > 0) {
            const names = underEstimated.slice(0, 3).map((t: any) => `"${t.name}" (base: ${t.avgBase.toFixed(1)}h → real: ${t.avgReal.toFixed(1)}h)`).join(', ');
            this.recommendations.push(
                `📈 Tareas subestimadas: ${names}. Incrementa los esfuerzos base para mejorar la precisión.`
            );
        }

        // Over-estimated (estimated > real, consistently)
        const overEstimated = this.taskStats.filter((t: any) => t.deviation < -15);
        if (overEstimated.length > 0) {
            const names = overEstimated.slice(0, 3).map((t: any) => `"${t.name}" (base: ${t.avgBase.toFixed(1)}h → real: ${t.avgReal.toFixed(1)}h)`).join(', ');
            this.recommendations.push(
                `📉 Tareas sobreestimadas: ${names}. Considera reducir los esfuerzos base para ajustarlos a la realidad.`
            );
        }

        // Overall deviation summary
        if (this.kpiTotalEstimated > 0 && this.kpiTotalReal > 0) {
            const sign = this.kpiDeviationPercent > 0 ? '+' : '';
            this.recommendations.push(
                `📊 Desviación global del catálogo: ${sign}${this.kpiDeviationPercent.toFixed(1)}% (estimado: ${this.kpiTotalEstimated.toFixed(0)}h, real: ${this.kpiTotalReal.toFixed(0)}h en ${this.filteredImputations.length} proyecto(s) con imputación).`
            );
        }

        // Low sample size
        if (this.filteredImputations.length < 5) {
            this.recommendations.push(
                `📋 Solo hay ${this.filteredImputations.length} proyecto(s) con imputación real. Se recomiendan al menos 5 para obtener estadísticas fiables.`
            );
        }

        // Good precision
        if (Math.abs(this.kpiDeviationPercent) < 10 && this.filteredImputations.length >= 3) {
            this.recommendations.push(
                `✅ La precisión general del catálogo es excelente (desviación ${this.kpiDeviationPercent > 0 ? '+' : ''}${this.kpiDeviationPercent.toFixed(1)}%). El catálogo está bien calibrado.`
            );
        }
    }

    // ====== CHARTS ======

    private renderUnitEffortChart(): void {
        const el = document.getElementById('cs-chart-unit-effort');
        if (!el) return;
        if (this.chartUnitEffort) this.chartUnitEffort.dispose();

        this.chartUnitEffort = echarts.init(el);
        const tasks = this.taskStats
            .filter((t: any) => t.avgBase > 0 || t.avgReal > 0)
            .sort((a: any, b: any) => Math.abs(b.deviation) - Math.abs(a.deviation))
            .slice(0, 10);
        const names = tasks.map((t: any) => t.name);

        this.chartUnitEffort.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            legend: { data: [this.variables?.effortBase || 'Base catálogo', this.variables?.realAvg || 'Real promedio'], bottom: 0 },
            grid: { left: '3%', right: '4%', bottom: '12%', top: '8%', containLabel: true },
            xAxis: { type: 'category', data: names, axisLabel: { rotate: 30, fontSize: 11 } },
            yAxis: { type: 'value', name: this.variables?.hours || 'Horas' },
            series: [
                {
                    name: this.variables?.effortBase || 'Base catálogo',
                    type: 'bar',
                    data: tasks.map((t: any) => +t.avgBase.toFixed(1)),
                    itemStyle: { color: '#6366f1', borderRadius: [4, 4, 0, 0] }
                },
                {
                    name: this.variables?.realAvg || 'Real promedio',
                    type: 'bar',
                    data: tasks.map((t: any) => +t.avgReal.toFixed(1)),
                    itemStyle: { color: '#f59e0b', borderRadius: [4, 4, 0, 0] }
                }
            ]
        });
    }

    private renderDeviationChart(): void {
        const el = document.getElementById('cs-chart-deviation');
        if (!el) return;
        if (this.chartDeviation) this.chartDeviation.dispose();

        this.chartDeviation = echarts.init(el);
        const tasks = this.taskStats
            .sort((a: any, b: any) => Math.abs(b.deviation) - Math.abs(a.deviation))
            .slice(0, 10);
        const names = tasks.map((t: any) => t.name);

        this.chartDeviation.setOption({
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const d = params[0];
                    return `${d.name}<br/>Desviación: <b>${d.value > 0 ? '+' : ''}${d.value}%</b>`;
                }
            },
            grid: { left: '3%', right: '4%', bottom: '8%', top: '8%', containLabel: true },
            xAxis: { type: 'value', name: '%', axisLabel: { formatter: '{value}%' } },
            yAxis: { type: 'category', data: names, axisLabel: { fontSize: 11 } },
            series: [{
                type: 'bar',
                data: tasks.map((t: any) => ({
                    value: +t.deviation.toFixed(1),
                    itemStyle: {
                        color: Math.abs(t.deviation) <= 10 ? '#10b981'
                            : Math.abs(t.deviation) <= 25 ? '#f59e0b'
                                : '#ef4444',
                        borderRadius: t.deviation >= 0 ? [0, 4, 4, 0] : [4, 0, 0, 4]
                    }
                })),
                label: {
                    show: true,
                    position: 'right',
                    formatter: (p: any) => `${p.value > 0 ? '+' : ''}${p.value}%`,
                    fontSize: 11
                }
            }]
        });
    }

    private renderPrecisionChart(): void {
        const el = document.getElementById('cs-chart-precision');
        if (!el) return;
        if (this.chartPrecision) this.chartPrecision.dispose();

        this.chartPrecision = echarts.init(el);

        const data: any[] = [];
        let maxVal = 0;
        this.filteredEstimates.forEach((est: any) => {
            const imp = this.filteredImputations.find((i: any) => i.estimateId === (est.id || est.idEstimate));
            if (imp && est.totalEffortHours) {
                const estimated = est.totalEffortHours;
                const actual = imp.totalActualHours;
                data.push([estimated, actual, est.name || est.idEstimate]);
                maxVal = Math.max(maxVal, estimated, actual);
            }
        });

        maxVal = Math.ceil(maxVal * 1.1);

        this.chartPrecision.setOption({
            tooltip: {
                formatter: (p: any) => {
                    return `${p.data[2]}<br/>Estimado: <b>${p.data[0].toFixed(1)}h</b><br/>Real: <b>${p.data[1].toFixed(1)}h</b>`;
                }
            },
            grid: { left: '3%', right: '4%', bottom: '10%', top: '8%', containLabel: true },
            xAxis: {
                type: 'value',
                name: this.variables?.totalEstimated || 'Total Estimado (h)',
                max: maxVal || 100
            },
            yAxis: {
                type: 'value',
                name: this.variables?.totalReal || 'Total Real (h)',
                max: maxVal || 100
            },
            series: [
                {
                    type: 'scatter',
                    data: data,
                    symbolSize: 14,
                    itemStyle: { color: '#6366f1', opacity: 0.8 }
                },
                {
                    type: 'line',
                    data: [[0, 0], [maxVal || 100, maxVal || 100]],
                    lineStyle: { color: '#94a3b8', type: 'dashed', width: 2 },
                    symbol: 'none',
                    tooltip: { show: false }
                }
            ]
        });
    }

    onResize(): void {
        this.chartUnitEffort?.resize();
        this.chartDeviation?.resize();
        this.chartPrecision?.resize();
    }

    getDeviationClass(deviation: number | null): string {
        if (deviation === null) return '';
        const abs = Math.abs(deviation);
        if (abs <= 10) return 'dev-good';
        if (abs <= 25) return 'dev-warn';
        return 'dev-bad';
    }
}
